import { FieldArray, FormikProps, useFormikContext } from 'formik'
import {
  BoxV4,
  InputStartAdornment,
  LabraToolTipV2,
  PlanType,
  TextInputV4,
} from '@labrav/react-components'
import { get, isNil } from 'lodash'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMemo } from 'react'

import clsx from 'clsx'
import { useStyles } from './UsagePlans.styles'
import {
  CombinedFormDimension,
  CombinedFormState,
} from '../../../../modules/combinedForm/reducer'
import { Product } from '../../../../modules/productsListing/reducer'
import { ShortenedNameV2 } from '@labrav/common'
import { faShelves } from '@fortawesome/pro-regular-svg-icons'

import { getCurrencySymbol } from '../../../../common/utils/getCurrencySymbol'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { HelpDrawer } from '@labrav/common'
import { useSelector } from '../../../../store'

interface StandardDimensionsV2Props {
  heading?: React.ReactNode
  subHeading?: React.ReactNode
  productId?: string
  productType?: PlanType | undefined
  disabled: boolean
  labraFormId?: string
}

export const UsagePlans = ({
  subHeading,
  heading,
  productType,
  disabled,
}: StandardDimensionsV2Props) => {
  const { classes } = useStyles({ productType })
  const { values, errors, touched, handleChange, handleBlur } =
    useFormikContext() as FormikProps<CombinedFormState>
  const dimensions = (values.resellerAgreementData.dimensions ||
    []) as CombinedFormDimension[]

  const productIdToUse = values.resellerAgreementData.awsProductId
  const products = useSelector<Product[]>(
    state => state.productsListing['AWS']?.products?.rows || []
  )
  const product =
    products.find(product => product.productId === productIdToUse) ||
    ({} as Product)
  const currencySymbol =
    getCurrencySymbol(values?.resellerAgreementData.currency as string) || '$'

  const allDimensions = useMemo(() => {
    return product?.productDimensions
  }, [product])
  const getAdditionalUsagePrice: any = (dimensionId: string) => {
    const matchedProductDimension = (allDimensions as any[])?.find(
      pd => pd.id === dimensionId
    )
    const additionalUsagePrice =
      matchedProductDimension?.prices?.additionalUsagePrice
    const subscriptionPrice = matchedProductDimension?.prices?.subscriptionPrice

    return additionalUsagePrice || subscriptionPrice
  }

  return (
    <>
      <BoxV4
        heading={'Usage plans'}
        headingIcon={
          <FontAwesomeIcon
            icon={faShelves}
            style={{ width: '18px', height: '14px' }}
          />
        }
        secondaryActions={
          <HelpDrawer
            sectionName={
              productType !== PlanType.SAAS_SUBSCRIPTION
                ? 'combinedFormUsagePlans'
                : 'combinedFormUsagePlansSubscription'
            }
            headerTitle="Help & FAQs"
          />
        }
      >
        <Typography className={classes.value}>
          Enter the discounted unit price you are offering to the ISV for each
          usage plan.
        </Typography>
        {heading && (
          <Typography className={classes.heading}>{heading}</Typography>
        )}
        {subHeading && (
          <Typography className={classes.value}>{subHeading}</Typography>
        )}
        <div className={clsx(classes.root, classes.elementsGap)}>
          <div className={classes.titleWraper}>
            <Typography
              className={clsx(classes.sectionText, classes.columnName)}
            >
              Plan
            </Typography>
            <Typography
              className={clsx(classes.sectionText, classes.columnDesc)}
            >
              Public price
            </Typography>
            <Typography
              className={clsx(classes.sectionText, classes.columnPrice)}
            >
              Price to ISV
            </Typography>
            {productType !== PlanType.SAAS_SUBSCRIPTION && !disabled && (
              <Typography
                className={clsx(
                  classes.sectionText,
                  classes.BuyerPrice,
                  classes.centerAlign
                )}
              >
                Price from buyer
                <LabraToolTipV2
                  tooltipPlacement="top"
                  description={
                    'The “Price from buyer” is automatically calculated by applying the above markup % to the price to ISV.'
                  }
                >
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    className={classes.toolTipIcon}
                  />
                </LabraToolTipV2>
              </Typography>
            )}
          </div>
          <div className={classes.border}></div>
          <FieldArray name="resellerAgreementData.dimensions">
            {() => {
              const filterdDimensions = dimensions.filter(dimension =>
                productType !== PlanType.SAAS_SUBSCRIPTION
                  ? dimension.type === 'additional_usage'
                  : true
              )
              return (
                <>
                  {dimensions
                    .filter(dimension =>
                      productType !== PlanType.SAAS_SUBSCRIPTION
                        ? dimension.type === 'additional_usage'
                        : true
                    )
                    .map((dimension, index) => {
                      const dimensionKey = dimension.dimensionId || index
                      const productAdditionalUsagePrice =
                        getAdditionalUsagePrice(dimension.dimensionId as string)

                      return (
                        <>
                          <div
                            className={classes.resourceRow}
                            data-testid="dimensions-row"
                          >
                            <div className={classes.resourcesContainer}>
                              <div
                                className={classes.columnName}
                                key={`resellerAgreementData.dimensions-${
                                  dimension.id || index
                                }-paymentDate`}
                              >
                                <div
                                  className={clsx(
                                    classes.alignDescription,
                                    classes.descHeightWidth
                                  )}
                                  key={`resellerAgreementData.dimensions-${dimensionKey}-name`}
                                  data-testid="dimension-name"
                                >
                                  <ShortenedNameV2
                                    name={dimensions[index].name || '-'}
                                    length={20}
                                  />
                                </div>
                              </div>
                              <div
                                className={clsx(
                                  classes.alignDescription,
                                  classes.wordWrap,
                                  classes.columnDesc
                                )}
                                key={`resellerAgreementData.dimensions-${dimensionKey}-desc`}
                                data-testid="description-select"
                              >
                                <Typography
                                  style={{ display: 'flex', fontSize: '14px' }}
                                >
                                  <span className={classes.currency}>
                                    {currencySymbol}
                                  </span>
                                  {productAdditionalUsagePrice}
                                </Typography>
                              </div>

                              {productType !== PlanType.SAAS_SUBSCRIPTION ? (
                                <div
                                  key={`dimensions-${dimensionKey}-price`}
                                  className={clsx(
                                    classes.columnPrice,
                                    classes.alignDescription
                                  )}
                                >
                                  <TextInputV4
                                    keyName={`resellerAgreementData.dimensions.${[index]}.prices.additionalUsagePrice`}
                                    title="Price"
                                    startAdornment={
                                      <InputStartAdornment
                                        text={currencySymbol}
                                      />
                                    }
                                    alignRight
                                    type="number"
                                    placeHolder=""
                                    value={
                                      !isNil(
                                        dimensions[index]?.prices
                                          ?.additionalUsagePrice
                                      )
                                        ? (dimensions[index]?.prices
                                            ?.additionalUsagePrice as number)
                                        : ''
                                    }
                                    updatedValue={
                                      !isNil(
                                        dimensions[index]?.prices
                                          ?.additionalUsagePrice
                                      )
                                        ? (dimensions[index]?.prices
                                            ?.additionalUsagePrice as number)
                                        : ''
                                    }
                                    isError={
                                      !!(
                                        get(
                                          touched,
                                          `resellerAgreementData.dimensions.${[index]}.prices.additionalUsagePrice`,
                                          false
                                        ) &&
                                        get(
                                          errors,
                                          `resellerAgreementData.dimensions.${[index]}.prices.additionalUsagePrice`,
                                          false
                                        )
                                      )
                                    }
                                    errorText={
                                      (!!get(
                                        touched,
                                        `resellerAgreementData.dimensions.${[index]}.prices.additionalUsagePrice`,
                                        false
                                      ) &&
                                        get(
                                          errors,
                                          `resellerAgreementData.dimensions.${[index]}.prices.additionalUsagePrice`,
                                          ''
                                        )) ||
                                      ''
                                    }
                                    onChange={handleChange}
                                    isReadOnly={disabled}
                                    isRequired={false}
                                    showSpinner={false}
                                    onBlur={handleBlur}
                                    keyToPrevent={['+', '-', 'e']}
                                  />
                                </div>
                              ) : (
                                <div
                                  key={`dimensions-${dimensionKey}-price`}
                                  className={clsx(
                                    classes.columnPrice,
                                    classes.alignDescription
                                  )}
                                >
                                  <TextInputV4
                                    keyName={`resellerAgreementData.dimensions.${[index]}.prices.subscriptionPrice`}
                                    title="Price"
                                    type="number"
                                    startAdornment={
                                      <InputStartAdornment
                                        text={currencySymbol}
                                      />
                                    }
                                    alignRight
                                    placeHolder=""
                                    value={
                                      !isNil(
                                        dimensions[index]?.prices
                                          ?.subscriptionPrice
                                      )
                                        ? (dimensions[index]?.prices
                                            ?.subscriptionPrice as number)
                                        : ''
                                    }
                                    updatedValue={
                                      !isNil(
                                        dimensions[index]?.prices
                                          ?.subscriptionPrice
                                      )
                                        ? (dimensions[index]?.prices
                                            ?.subscriptionPrice as number)
                                        : ''
                                    }
                                    isError={
                                      !!(
                                        get(
                                          touched,
                                          `resellerAgreementData.dimensions.${[index]}.prices.subscriptionPrice`,
                                          false
                                        ) &&
                                        get(
                                          errors,
                                          `resellerAgreementData.dimensions.${[index]}.prices.subscriptionPrice`,
                                          false
                                        )
                                      )
                                    }
                                    errorText={
                                      (!!get(
                                        touched,
                                        `resellerAgreementData.dimensions.${[index]}.prices.subscriptionPrice`,
                                        false
                                      ) &&
                                        get(
                                          errors,
                                          `resellerAgreementData.dimensions.${[index]}.prices.subscriptionPrice`,
                                          ''
                                        )) ||
                                      ''
                                    }
                                    onChange={handleChange}
                                    isReadOnly={disabled}
                                    isRequired={false}
                                    showSpinner={false}
                                    onBlur={handleBlur}
                                    keyToPrevent={['+', '-', 'e']}
                                  />
                                </div>
                              )}
                              {productType !== PlanType.SAAS_SUBSCRIPTION &&
                                !disabled && (
                                  <div
                                    className={clsx(
                                      classes.alignDescription,
                                      classes.wordWrap,
                                      classes.BuyerPrice
                                    )}
                                    key={`dimensions-${dimensionKey}-desc`}
                                    data-testid="description-select"
                                  >
                                    {dimensions?.[index]?.prices
                                      ?.additionalUsagePrice ? (
                                      <Typography
                                        style={{
                                          display: 'flex',
                                          fontSize: '14px',
                                        }}
                                      >
                                        <span className={classes.currency}>
                                          {currencySymbol}
                                        </span>
                                        {(() => {
                                          const basePrice =
                                            dimensions?.[index]?.prices
                                              ?.additionalUsagePrice
                                          const adjustment = values?.cppoData
                                            ?.adjustmentPercentage as number

                                          const result =
                                            (adjustment * basePrice) / 100 +
                                            basePrice

                                          return Number.isInteger(result)
                                            ? result
                                            : Number(result?.toFixed(2))
                                        })()}
                                      </Typography>
                                    ) : (
                                      '-'
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                          {index !== filterdDimensions.length - 1 && (
                            <div className={classes.border}></div>
                          )}
                        </>
                      )
                    })}
                </>
              )
            }}
          </FieldArray>
        </div>
      </BoxV4>
    </>
  )
}
